import type { WebTabId } from "./tabBar";
import { syncStickyChromeOffsets } from "@shared/ui/stickyTableHead";

const STICKY_CLASS = "sfe-sticky-tab-nav";
const STUCK_CLASS = "sfe-sticky-tab-nav--stuck";
const NATIVE_STUCK_CLASS = "is-stuck";
const HEADER_STUCK_CLASS = "sfe-sticky-chrome--stuck";

/** Analytics+ sticky strip + dimmed site header — enabled for every wheel tab. */
export function shouldStickyTabNav(_activeTab: WebTabId): boolean {
  return true;
}

/** Extension-style sticky tab strip + dimmed site header when scrolled. */
export function mountWebStickyTabNav(
  navTarget: HTMLElement,
  siteHeader: HTMLElement | null,
  isStickyEnabled: () => boolean,
): { sync: () => void; teardown: () => void } {
  document.documentElement.style.setProperty("--sfe-sticky-chrome-bg", "var(--nav-backdrop, #12151a)");
  navTarget.style.setProperty("--sfe-sticky-tab-nav-bg", "var(--nav-backdrop, #12151a)");

  let sentinel =
    navTarget.previousElementSibling?.classList.contains("wheel-subnav-sentinel")
      ? (navTarget.previousElementSibling as HTMLElement)
      : null;

  if (!sentinel) {
    const created = document.createElement("div");
    created.className = "wheel-subnav-sentinel sfe-sticky-tab-nav-sentinel";
    created.setAttribute("data-sfe-sticky-sentinel", "true");
    created.setAttribute("aria-hidden", "true");
    navTarget.parentElement?.insertBefore(created, navTarget);
    sentinel = created;
  }

  let observer: IntersectionObserver | null = null;
  let raf = 0;

  const publishOffsets = (): void => {
    syncStickyChromeOffsets();
  };

  const scheduleOffsets = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      publishOffsets();
    });
  };

  const applyStuck = (stuck: boolean): void => {
    if (!isStickyEnabled()) {
      navTarget.classList.remove(STUCK_CLASS, NATIVE_STUCK_CLASS);
      siteHeader?.classList.remove(HEADER_STUCK_CLASS);
      publishOffsets();
      return;
    }
    navTarget.classList.toggle(STUCK_CLASS, stuck);
    navTarget.classList.toggle(NATIVE_STUCK_CLASS, stuck);
    siteHeader?.classList.toggle(HEADER_STUCK_CLASS, stuck);
    publishOffsets();
  };

  const ensureObserver = (): void => {
    if (observer || !sentinel) return;
    observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry || !isStickyEnabled()) return;
        applyStuck(!entry.isIntersecting);
      },
      { threshold: [0] },
    );
    observer.observe(sentinel);
  };

  const sync = (): void => {
    const enabled = isStickyEnabled();
    navTarget.classList.toggle(STICKY_CLASS, enabled);
    navTarget.classList.toggle("wheel-subnav", true);

    if (!enabled) {
      applyStuck(false);
      return;
    }

    ensureObserver();
    // Recompute stuck state immediately after tab switches (observer may not fire).
    if (sentinel) {
      const rect = sentinel.getBoundingClientRect();
      applyStuck(rect.bottom < 0 || rect.top < 0);
    } else {
      publishOffsets();
    }
  };

  window.addEventListener("resize", scheduleOffsets, { passive: true });
  window.addEventListener("scroll", scheduleOffsets, { passive: true, capture: true });
  sync();

  return {
    sync,
    teardown: () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", scheduleOffsets);
      window.removeEventListener("scroll", scheduleOffsets, true);
      observer?.disconnect();
      observer = null;
      navTarget.classList.remove(STICKY_CLASS, STUCK_CLASS, NATIVE_STUCK_CLASS);
      siteHeader?.classList.remove(HEADER_STUCK_CLASS);
    },
  };
}

export function createStickySync(
  getActiveTab: () => WebTabId,
  navTarget: HTMLElement,
  siteHeader: HTMLElement | null,
): { sync: () => void; teardown: () => void } {
  return mountWebStickyTabNav(navTarget, siteHeader, () =>
    shouldStickyTabNav(getActiveTab()),
  );
}
